import { ApolloError } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { type FC, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { ActionButton, Callout, CalloutIntent, TextField, TriggerButton, VisuallyHidden, VStack } from "widgets";
import type z from "zod";
import { object, string } from "zod";

import { useFormAria } from "@/app-utils/form/useFormAria.ts";
import { broadcast_logout, type LogoutMessage } from "@/modules/app/messaging/broadcast.ts";

const loginSchema = object({
  username: string({ required_error: "Username is required" }).trim().nonempty({ message: "Username is required" }),
  password: string({ required_error: "Password is required" }).trim().nonempty({ message: "Password is required" }),
});

export type LoginFormData = z.infer<typeof loginSchema>;

export interface LoginFormProps {
  onSubmit: (data: LoginFormData) => Promise<unknown>;
}

export const LoginForm: FC<LoginFormProps> = (props) => {
  const [isReadable, setReadability] = useState(false);

  const {
    handleSubmit,
    register,
    reset,
    setError,
    setFocus,
    formState: { errors, isSubmitting },
  } = useFormAria({
    defaultValues: { username: "", password: "" },
    resolver: zodResolver(loginSchema),
  });

  const onSubmit: SubmitHandler<LoginFormData> = async (data) => {
    try {
      broadcast_logout?.postMessage({ reload: true } satisfies LogoutMessage);
      await props.onSubmit(data);
      reset();
    } catch (error) {
      let errorMessage = "Authentication failed"; // Default message

      if (error instanceof ApolloError) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        try {
          const errorResponse = JSON.parse(error.message);
          errorMessage = errorResponse?.errors?.[0]?.message || errorMessage;
        } catch {
          // Keep default message if JSON parsing fails
        }
      }

      setError("root", { message: errorMessage });

      setFocus("username");
    }
  };

  return (
    <VStack spacing={15}>
      <form data-testid="login" onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={10}>
          <TextField
            label="Username"
            {...register("username")}
            autoFocus
            autoComplete="login"
            isRequired
            isInvalid={!!errors.username}
            errorMessage={errors.username?.message?.toString()}
            data-testid="username"
          />
          <TextField
            label="Password"
            {...register("password")}
            type={isReadable ? "text" : "password"}
            action={
              <TriggerButton
                onPress={() => {
                  setReadability(!isReadable);
                }}
                icon={isReadable ? "visibility_off" : "visibility"}
                label={`${isReadable ? "Hide" : "Show"} Password`}
              />
            }
            autoComplete="current-password"
            isRequired
            isInvalid={!!errors.password}
            errorMessage={errors.password?.message?.toString()}
            data-testid="password"
          />
        </VStack>
        <VisuallyHidden>
          {/* Just useful when the user presses "Enter" */}
          <button type="submit" disabled={isSubmitting} tabIndex={-1} />
        </VisuallyHidden>
      </form>
      {!!errors.root && (
        <Callout intent={CalloutIntent.Danger} domProps={{ role: "alert" }}>
          {errors.root.message}
        </Callout>
      )}
      <ActionButton onPress={() => handleSubmit(onSubmit)()} label="Sign In" isLoading={isSubmitting} />
    </VStack>
  );
};
